import * as THREE from 'three/webgpu';
import {
  attribute,
  float,
  mix,
  positionGeometry,
  smoothstep,
  sqrt,
  uniform,
  uv,
  varying,
  vec3,
  mod as tslMod,
} from 'three/tsl';

import { uNight, uRainAmt, uTime } from '../core/uniforms.js';
import { mulberry32 } from '../math/rng.js';
import { mod, saturate } from '../math/mathx.js';
import { RENDER_ORDER } from './materials.js';

/**
 * @file THE RAIN. One instanced draw, ~2200 streak quads, and NOT ONE BYTE of
 * per-frame CPU work in the particle path.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THERE IS NO PARTICLE ARRAY
 * ─────────────────────────────────────────────────────────────────────────────
 * A drop's whole life is a pure function of its INSTANCE ID and `uTime`,
 * evaluated in the vertex stage. The id is hashed once, at build time, into
 * three numbers ({@link rainHash} -> the `iSeed` attribute); everything else —
 * where it is, how far it has fallen, how visible it is — falls out of
 * `mod()`. So the per-frame cost of 2200 drops is: two `vec3` uniform writes
 * (the column origin and the camera's right vector) and one draw. There is no
 * buffer to re-upload, no pool to age, and nothing to keep in step across a
 * pause.
 *
 * The maths is mirrored EXACTLY in JS below ({@link rainDropXZ},
 * {@link rainFall}, {@link rainShape}) so `tests/weather.test.mjs` can
 * property-test it — cylinder bounds, wrap continuity, hash determinism —
 * without a GPU. The mirror is the SPECIFICATION of the shader graph; it is not
 * bit-exact with it (float32 vs double), and nothing at runtime compares them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **The column is a SQUARE that wraps, and a CIRCLE that fades.** Drops are
 *   laid out on a `2R` square tile and wrapped around the camera target
 *   (`mod(b - o + R, 2R) - R`), which keeps every drop at a FIXED WORLD
 *   POSITION — so driving through rain gives real parallax instead of a
 *   curtain glued to the windscreen. The price is that a drop teleports by
 *   `2R` when it crosses the tile edge; the radial alpha fade is what makes
 *   that invisible, because it has already reached 0 by the time the edge
 *   arrives. Move `RAIN.fadeR` up toward 1 and the pops come back.
 *
 * • **`uRainAmt` MUST NOT touch the fall speed.** The fall phase is
 *   `mod(seed*span + uTime*speed, span)`: scale `speed` and every drop's phase
 *   jumps by `uTime * dSpeed`, which over the weather system's 6-12 s ramp is
 *   hundreds of metres — the whole field shears. Intensity therefore rides
 *   COUNT (`instanceCount`), streak LENGTH and ALPHA only, all of which are
 *   phase-free. The same reasoning is why `render/ocean.js`'s `rainChop` scales
 *   an amplitude and not a rate.
 *
 * • **The streak is WORLD-VERTICAL, not screen-aligned.** Rain falls along -Y
 *   and at a 52-degree pitch a world-vertical segment projects to ~62% of its
 *   length up-screen, which is exactly what rain looks like from a helicopter.
 *   The quad is billboarded about that axis using the camera's world RIGHT
 *   vector, handed in as a uniform rather than pulled out of `cameraWorldMatrix`
 *   in TSL — one `Vector3` copy a frame against a matrix-column accessor whose
 *   spelling changes between three releases.
 *
 * • **The cross-streak fade is a FRAGMENT-stage read of `uv`.** It cannot be a
 *   vertex one: a triangular profile needs a peak in the MIDDLE of the quad and
 *   the quad has no middle vertex, so a vertex-stage `1 - |2u-1|` is 0 at both
 *   ends and interpolates to 0 everywhere. This is NOT a violation of the
 *   decode-integers-in-the-vertex-stage rule — that rule is about `floor()` on
 *   a near-integer varying; a smooth ramp is exactly what a varying is for. The
 *   per-DROP half of the alpha does go through `varying()`.
 *
 * • **Colours sit deliberately under the bloom threshold** (day 0.85, night
 *   0.45 — see `render/post.js`). Rain is a translucent grey veil; the moment
 *   it blooms it stops being water and becomes sparks.
 *
 * @module engine/render/weather
 */

/** @typedef {import('three/tsl').ShaderNodeObject<any>} TSLNode */

/**
 * The rain's shape constants. Exported because the JS mirror, the shader graph
 * and the test all have to agree on them, and a second copy of `span` is a
 * silent one-frame-in-a-hundred pop that nobody will ever find.
 */
export const RAIN = Object.freeze({
  /** Column radius, metres. The visible cylinder; the tile is `2 * radius`. */
  radius: 28,
  /** Vertical extent of the falling column, metres. */
  span: 17,
  /** Metres above the origin (the camera target) a drop is born. */
  top: 15.6,
  /** Fall speed, m/s. A CONSTANT — see the header. */
  speed: 14,
  /** Streak length at full storm, metres. */
  length: 0.45,
  /**
   * Streak width, metres. ⚠ TUNED IN PIXELS, NOT IN MILLIMETRES. The city frames
   * ~27 m across 780 px at `distance: 34`, i.e. ~29 px/m, so the physically
   * honest 2-3 mm of a real raindrop is a QUARTER of a pixel — and a sub-pixel
   * quad is a coin flip against the sample grid, which is exactly what the first
   * browser pass showed: 2200 drops in the column and about thirty on screen.
   * 0.055 m is ~1.6 px, which always rasterises. The triangular cross-profile
   * (see the header) is what keeps it reading as a thread rather than a bar.
   */
  width: 0.055,
  /** Lateral lean of the streak per unit of its length (the wind). */
  windX: 0.2,
  windZ: -0.09,
  /** Radial fade: full alpha inside `fadeR * radius`, gone by `0.97 * radius`. */
  fadeR: 0.7,
  /** Metres of fade at the top of the column (hides the wrap). */
  fadeTop: 1.6,
  /** Metres of fade at the bottom (drops die at the ground, not through it). */
  fadeBottom: 1.6,
  /** Peak opacity of a streak's centre line. */
  opacity: 0.5,
  /** Day / night streak colour. Both under the bloom threshold on purpose. */
  colorDay: [0.72, 0.76, 0.84],
  colorNight: [0.3, 0.34, 0.42],
});

/** Per-stream salts for {@link rainHash}: x-tile, z-tile, fall phase. */
const HASH_SALT = [0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];

/**
 * The drop hash: a PURE function of the instance id, with no seed and no
 * `Math.random`. Three independent streams per id.
 *
 * Built on `mulberry32` rather than the usual `fract(sin(x) * 43758.5)` because
 * that classic loses all its precision above a few thousand — and 2200 drops is
 * already there. This runs ONCE, at build time, into a static attribute; the
 * shader never hashes anything.
 *
 * @param {number} i Instance id, 0-based.
 * @param {number} [k] Stream, 0..2.
 * @returns {number} [0, 1)
 */
export function rainHash(i, k = 0) {
  return mulberry32((Math.imul(i + 1, 0x27d4eb2d) + HASH_SALT[k % 3]) >>> 0)();
}

/**
 * WORLD XZ of drop `i` for a column centred on `(ox, oz)`, plus the offset from
 * that centre — which is what the radial fade is a function of.
 *
 * The JS mirror of the shader's tile wrap. See the header for why the wrap is
 * square and the fade is round.
 *
 * @param {number} i
 * @param {number} ox @param {number} oz Column centre (the camera target).
 * @param {typeof RAIN} [o]
 * @returns {{x: number, z: number, dx: number, dz: number, r: number}}
 */
export function rainDropXZ(i, ox, oz, o = RAIN) {
  const side = o.radius * 2;
  const bx = (rainHash(i, 0) - 0.5) * side;
  const bz = (rainHash(i, 1) - 0.5) * side;
  const dx = mod(bx - ox + o.radius, side) - o.radius;
  const dz = mod(bz - oz + o.radius, side) - o.radius;
  return { x: ox + dx, z: oz + dz, dx, dz, r: Math.hypot(dx, dz) };
}

/**
 * Metres drop `i` has fallen from the top of the column at `time`. Wraps in
 * `[0, span)`.
 * @param {number} i
 * @param {number} time Seconds (`uTime`).
 * @param {typeof RAIN} [o]
 * @returns {number}
 */
export function rainFall(i, time, o = RAIN) {
  return mod(rainHash(i, 2) * o.span + time * o.speed, o.span);
}

/**
 * The per-drop alpha SHAPE — radial x top x bottom, before `uRainAmt` and
 * before the cross-streak profile. 0 means the drop is invisible, which is the
 * property that makes the tile wrap unnoticeable.
 *
 * @param {number} i
 * @param {number} time
 * @param {number} ox @param {number} oz
 * @param {typeof RAIN} [o]
 * @returns {number} 0..1
 */
export function rainShape(i, time, ox, oz, o = RAIN) {
  const { r } = rainDropXZ(i, ox, oz, o);
  const fall = rainFall(i, time, o);
  const aR = 1 - smooth01(o.radius * o.fadeR, o.radius * 0.97, r);
  const aTop = smooth01(0, o.fadeTop, fall);
  const aBot = 1 - smooth01(o.span - o.fadeBottom, o.span, fall);
  return aR * aTop * aBot;
}

/**
 * @typedef {Object} RainHandle
 * @property {THREE.Mesh} object The single draw. Already parented.
 * @property {(x: number, y: number, z: number) => void} follow Moves the column
 *   origin. Call once a frame with the camera TARGET (the player's chest), not
 *   the camera position — the cylinder has to be centred on what you are
 *   looking at, or half of it falls behind the camera.
 * @property {(camera: THREE.Camera) => void} syncCamera Refreshes the billboard
 *   axis from the camera's world matrix. Cheap; call it next to `follow`.
 * @property {(amt: number) => void} setAmount 0..1. Drives `instanceCount` and
 *   `visible`; the SHADER reads `uRainAmt` for length and alpha. At 0 the mesh
 *   is hidden and costs nothing at all.
 * @property {(on: boolean) => void} setEnabled A MASTER SWITCH over
 *   {@link RainHandle.setAmount}, for somewhere the sky cannot reach — an
 *   interior. Disabled, the mesh is hidden and `follow`/`syncCamera` return
 *   immediately, so the weather system may keep driving `setAmount` at whatever
 *   the sky is doing outside and nothing draws. Re-enabling restores the
 *   amount that was set meanwhile. Enabled by default.
 * @property {() => {max: number, drawn: number, amount: number, enabled: boolean}} stats
 * @property {() => void} dispose Idempotent.
 */

/**
 * Builds the rain.
 *
 * @param {Object} [o]
 * @param {THREE.Object3D|null} [o.parent] Added here; must be at IDENTITY (the
 *   engine scene or a level root), because the shader works in world space.
 * @param {number} [o.count=2200] Streaks at full storm.
 * @param {Partial<typeof RAIN>} [o.tune] Overrides for {@link RAIN}.
 * @param {string} [o.name='rain']
 * @returns {RainHandle}
 */
export function createRain(o = {}) {
  const { parent = null, count = 2200, tune = null, name = 'rain' } = o;
  const R = tune ? Object.freeze({ ...RAIN, ...tune }) : RAIN;
  const max = Math.max(1, Math.floor(count));

  // ── geometry: one quad, `max` instances ────────────────────────────────────
  // Non-indexed (project rule 3) and tiny: 6 verts total, whatever `max` is.
  // Corner convention: x in {-0.5, +0.5} across the streak, y in {0, 1} ALONG
  // it, with y = 0 the leading (lower) end — so the wind lean is `y * wind`.
  const corners = new Float32Array([
    -0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0,
    -0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0,
  ]);
  const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 0, 1]);

  const geo = new THREE.InstancedBufferGeometry();
  geo.name = name;
  geo.setAttribute('position', new THREE.BufferAttribute(corners, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));

  const seeds = new Float32Array(max * 3);
  for (let i = 0; i < max; i++) {
    seeds[i * 3] = rainHash(i, 0);
    seeds[i * 3 + 1] = rainHash(i, 1);
    seeds[i * 3 + 2] = rainHash(i, 2);
  }
  geo.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seeds, 3));
  geo.instanceCount = 0;
  // The column moves with the camera and is never worth testing against a
  // frustum; a finite sphere here would also be a lie the moment `follow` runs.
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

  // ── uniforms ───────────────────────────────────────────────────────────────
  const uOrigin = uniform(new THREE.Vector3());
  const uRight = uniform(new THREE.Vector3(1, 0, 0));

  const mat = new THREE.MeshBasicNodeMaterial();
  mat.name = name;
  mat.transparent = true;
  // Depth TEST on, depth WRITE off: a building in front of a streak occludes it
  // (which is most of what sells rain in a city), while streaks never occlude
  // each other into a sorting problem.
  mat.depthWrite = false;
  mat.depthTest = true;
  mat.side = THREE.DoubleSide;
  mat.fog = true;
  mat.vertexColors = false;

  {
    const h = attribute('iSeed', 'vec3');
    const side = float(R.radius * 2);
    const rad = float(R.radius);

    const bx = h.x.sub(0.5).mul(side);
    const bz = h.y.sub(0.5).mul(side);
    // `mod` is FLOOR-mod on both backends (WGSL gets `x - y*floor(x/y)`), which
    // is what makes a negative offset wrap instead of mirroring.
    const dx = tslMod(bx.sub(uOrigin.x).add(rad), side).sub(rad);
    const dz = tslMod(bz.sub(uOrigin.z).add(rad), side).sub(rad);
    const fall = tslMod(h.z.mul(float(R.span)).add(uTime.mul(float(R.speed))), float(R.span));

    // Streak length grows with the storm; the SPEED never does (header).
    const len = float(R.length).mul(float(0.55).add(uRainAmt.mul(0.45)));
    const c = positionGeometry;

    const centre = vec3(
      uOrigin.x.add(dx),
      uOrigin.y.add(float(R.top)).sub(fall),
      uOrigin.z.add(dz),
    );
    const along = vec3(float(R.windX), float(1), float(R.windZ)).mul(c.y.mul(len));
    mat.positionNode = centre.add(uRight.mul(c.x.mul(float(R.width)))).add(along);

    // Per-drop alpha, VERTEX stage (it is constant over the quad).
    const r = sqrt(dx.mul(dx).add(dz.mul(dz)));
    const aR = smoothstep(float(R.radius * R.fadeR), float(R.radius * 0.97), r).oneMinus();
    const aTop = smoothstep(float(0), float(R.fadeTop), fall);
    const aBot = smoothstep(float(R.span - R.fadeBottom), float(R.span), fall).oneMinus();
    // A drizzle is not just fewer drops, it is fainter ones.
    const amt = float(0.35).add(uRainAmt.mul(0.65));
    const dropAlpha = varying(aR.mul(aTop).mul(aBot).mul(amt), 'rainDropAlpha');

    // Cross-streak triangular profile, FRAGMENT stage (header explains why).
    const profile = uv().x.mul(2).sub(1).abs().oneMinus();

    mat.colorNode = mix(
      vec3(R.colorDay[0], R.colorDay[1], R.colorDay[2]),
      vec3(R.colorNight[0], R.colorNight[1], R.colorNight[2]),
      uNight,
    );
    mat.opacityNode = dropAlpha.mul(profile).mul(float(R.opacity));
  }

  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = name;
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();
  mesh.renderOrder = RENDER_ORDER.emissive;
  mesh.visible = false;
  parent?.add(mesh);

  let amount = 0;
  let drawn = 0;
  let disposed = false;
  // The interior switch. It sits UNDER `setAmount` rather than beside it because
  // `systems/Weather.js` owns the amount and keeps writing it every update — a
  // switch that only hid the mesh would be undone by the very next sky tick.
  let enabled = true;

  return {
    object: mesh,

    follow(x, y, z) {
      if (!enabled) return;
      uOrigin.value.set(x, y, z);
    },

    syncCamera(camera) {
      if (!enabled || !camera) return;
      // Column 0 of the camera's world matrix IS its right vector, already
      // normalised and (at any roll-free pitch) already horizontal.
      const e = camera.matrixWorld.elements;
      uRight.value.set(e[0], e[1], e[2]);
    },

    setAmount(a) {
      if (disposed) return;
      amount = saturate(a);
      drawn = amount > 0.002 ? Math.max(1, Math.round(max * amount)) : 0;
      geo.instanceCount = drawn;
      mesh.visible = enabled && drawn > 0;
    },

    setEnabled(on) {
      if (disposed) return;
      const next = !!on;
      if (next === enabled) return;
      enabled = next;
      // `drawn`/`instanceCount` are left alone: the amount is the sky's, not
      // ours, and re-enabling has to come back to whatever it is by then.
      // Coming back, the column origin is one frame stale — it is wherever the
      // caller last was outside, which is the door they walk back out of — and
      // the next `follow` fixes it.
      mesh.visible = enabled && drawn > 0;
    },

    stats() {
      return { max, drawn, amount, enabled };
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      mesh.removeFromParent();
      geo.dispose();
      mat.dispose();
    },
  };
}

/**
 * Hermite smoothstep. Local (not `math/mathx.js#smooth`) only so the JS mirror
 * reads in the same order as the shader lines it mirrors.
 * @param {number} e0 @param {number} e1 @param {number} x
 * @returns {number}
 */
function smooth01(e0, e1, x) {
  const t = e1 === e0 ? 0 : saturate((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
